import Help16 from '@carbon/icons-react/es/help/16';
import Logout16 from '@carbon/icons-react/es/logout/16';
import Money16 from '@carbon/icons-react/es/money/16';
import Settings16 from '@carbon/icons-react/es/settings/16';
import UserAvatar16 from '@carbon/icons-react/es/user--avatar/16';
import React, { FC, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { User } from 'api/models';
import { Menu as MenuAtom } from 'components/atoms/dropdown';
import { Tag } from 'components/atoms/tag';
import { getPlanName } from 'components/organisms/module-settings/billing/utils';
import { RoutePaths, KBLinks, PermissionRuleName } from 'constant';
import { useBillingFeatureFlag } from 'hooks/use-account';
import { useAccountDataQuery } from 'queries/account';
import { UserCard } from './user-card';

const Menu = styled(MenuAtom)`
  border: none;
  padding: 0;
`;
const MenuItem = styled(MenuAtom.ItemWithPermission)`
  && {
    padding: 0 var(--spacing-05);
    height: 32px;
    margin: 0;
  }
`;

const UserCardMenuItem = styled(MenuItem)`
  &&& {
    height: 64px;
  }
`;

const MenuDivider = styled(MenuAtom.Divider)`
  margin: var(--spacing-04);
`;

interface ProfileMenuProps {
  user: User;
  hide: () => void;
}

const StyledLink = styled(Link)`
  width: 100%;
`;

const StyledTag = styled(Tag)`
  margin-left: var(--spacing-06);
`;

export const ProfileMenu: FC<ProfileMenuProps> = ({ user, hide }) => {
  const { pathname } = useLocation();
  const isBillingEnabled = useBillingFeatureFlag();
  const { data: accountData } = useAccountDataQuery();
  const currentPlan = useMemo(
    () =>
      accountData?.billingMeta?.subscription
        ? getPlanName(accountData.billingMeta.subscription.plan.planName)
        : null,
    [accountData],
  );

  return (
    <Menu selectedKeys={[pathname]}>
      <UserCardMenuItem onClick={hide}>
        <StyledLink to={RoutePaths.profile}>
          <UserCard user={user} />
        </StyledLink>
      </UserCardMenuItem>
      <MenuItem
        icon={<UserAvatar16 />}
        id="profile"
        onClick={hide}
        key={RoutePaths.profile}
      >
        <Link to={RoutePaths.profile}>
          <FormattedMessage id="profile" />
        </Link>
      </MenuItem>
      {isBillingEnabled ? (
        <MenuItem
          icon={<Money16 />}
          id="billing"
          onClick={hide}
          key={RoutePaths.billingSettings}
          ruleName={PermissionRuleName.BillingPage}
        >
          <Link to={RoutePaths.billingSettings}>
            <FormattedMessage id="settings.billing.title" />
          </Link>
          {currentPlan && (
            <StyledTag color="green">
              <FormattedMessage
                id={`billing.plan.${currentPlan}`.toLowerCase()}
              />
            </StyledTag>
          )}
        </MenuItem>
      ) : null}
      <MenuItem
        icon={<Settings16 />}
        id="settings"
        onClick={hide}
        key={RoutePaths.notificationsSettings}
      >
        <Link to={RoutePaths.notificationsSettings}>
          <FormattedMessage id="settings" />
        </Link>
      </MenuItem>
      <MenuDivider />
      <MenuItem
        icon={<Help16 />}
        id="help"
        onClick={hide}
        key={RoutePaths.settings}
      >
        <a href={KBLinks.helpCenter} target="_blank" rel="noreferrer">
          <FormattedMessage id="help_center" />
        </a>
      </MenuItem>
      <MenuDivider />
      <MenuItem icon={<Logout16 />} id="logout" onClick={hide}>
        <Link to={RoutePaths.logout}>
          <FormattedMessage id="logout" />
        </Link>
      </MenuItem>
    </Menu>
  );
};
